import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useReminderSettings } from "@/features/business/hooks/reminderSettingsQueries";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { ReminderSettingsSection } from "../components/ReminderSettingsSection";

const header = <PageHeader title="Reminders & emails" description="Manage outgoing customer emails for your business." />;

function ReminderSettingsLoader({ businessId, businessName }: { businessId: string; businessName: string }) {
  const query = useReminderSettings(businessId);

  if (!query.data) {
    return (
      <Page>
        {header}
        <PageContent>
          {query.error ? (
            <ErrorState title="Couldn't load reminders" message={query.error.message} onRetry={() => void query.refetch()} />
          ) : <LoadingSpinner message="Loading reminder settings..." />}
        </PageContent>
      </Page>
    );
  }

  return (
    <ReminderSettingsSection
      header={header}
      businessId={businessId}
      businessName={businessName}
      settings={query.data}
      refreshError={query.error?.message}
      onRetry={() => void query.refetch()}
      onReload={async () => {
        const result = await query.refetch();
        if (result.error) throw result.error;
        if (!result.data) throw new Error("No reminder settings were returned.");
        return result.data;
      }}
    />
  );
}

export function CustomerEmailsPage() {
  const { user } = useAuth();
  const { business, businessId, loading } = useCurrentBusiness();

  if (!loading && business && businessId && user && business.ownerId === user.id) {
    return <ReminderSettingsLoader key={`${user.id}:${businessId}`} businessId={businessId} businessName={business.name} />;
  }

  return (
    <Page>
      {header}
      <PageContent>
        {loading ? <LoadingSpinner message="Loading business..." /> : !business || !businessId ? (
          <ErrorState message="Select a business to manage customer emails." />
        ) : (
          <ErrorState message="Only the business owner can manage customer email settings." />
        )}
      </PageContent>
    </Page>
  );
}
